/**
 * Per-player running score: V4.2.T9.
 *
 * The drawing-phase left panel shows each player's score AS THE GAME RUNS
 * (not just at gameOver). The canonical source is the same one the gameOver
 * scoreboard uses: `aggregateScoreboard` walks `game.turnSummaries` (drawer
 * points + per-guesser audience points) and rolls up a `total` per player.
 *
 * This helper flattens that into a `playerId → total` Map so the panel can
 * look up a row's score in O(1). It REUSES `aggregateScoreboard` (the 3-strike
 * rule): no second scoring path. Scores reflect COMPLETED turns only; the
 * in-flight turn's `correctGuesserIds` is not yet a `TurnSummary`, so it does
 * not move the totals until the turn ends (matching gameOver semantics).
 */

import type { SerializedPlayer } from "@drawlol/shared";
import { aggregateScoreboard } from "./scoreboard.js";

/**
 * Build a `playerId → running total` map from the game's turn summaries.
 *
 * @param players       Current roster (carries id + name).
 * @param turnSummaries Completed-turn rollups from the snapshot.
 * @returns A Map; every roster player is present (0 if they never scored).
 */
export function buildTurnScores(
  players: ReadonlyArray<Pick<SerializedPlayer, "id" | "name">>,
  turnSummaries: Parameters<typeof aggregateScoreboard>[0],
): ReadonlyMap<string, number> {
  const roster = new Map(players.map((p) => [p.id, p]));
  const board = aggregateScoreboard(turnSummaries, roster);
  return new Map(board.map((entry) => [entry.playerId, entry.total]));
}
